import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Button } from '@shared/components';
import { useCustomerReviews } from '../hooks/useCustomerReviews';
import { useDeliveredPackages } from '../hooks/useDeliveredPackages';
import StarRatingInput from '../components/reviews/StarRatingInput';
import ReviewStatusBadge from '../components/reviews/ReviewStatusBadge';

export const ReviewsRoute = '/reviews';
export const ReviewsPageName = 'Reviews';

const BODY_LIMIT = 1000;
const TITLE_LIMIT = 120;

const Reviews = () => {
  const [searchParams] = useSearchParams();
  const { reviews, isLoading, isSubmitting, submitReview } = useCustomerReviews();
  const { packages: delivered } = useDeliveredPackages();

  const initialPackageId = searchParams.get('packageId') || '';
  const initialRating = Number(searchParams.get('rating')) || 5;

  const [rating, setRating] = useState(initialRating);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [packageId, setPackageId] = useState(initialPackageId);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (initialPackageId) setPackageId(initialPackageId);
  }, [initialPackageId]);

  const reviewedPackageIds = useMemo(
    () => new Set(reviews.map((r) => r.packageId).filter(Boolean) as number[]),
    [reviews]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating) {
      setFormError('Please choose a star rating.');
      return;
    }
    if (!body.trim()) {
      setFormError('Please write a few words about your experience.');
      return;
    }
    setFormError(null);
    const ok = await submitReview({
      rating,
      title: title.trim() || undefined,
      body: body.trim(),
      packageId: packageId ? Number(packageId) : undefined,
    });
    if (ok) {
      setTitle('');
      setBody('');
      setPackageId('');
      setRating(5);
    }
  };

  const inputClass =
    'mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-white';

  return (
    // pb-24 keeps the last review clear of the fixed MobileNav (hidden at md+).
    <div className="mx-auto max-w-3xl space-y-6 px-4 pt-4 pb-24 sm:px-6 sm:pt-6 md:pb-6" data-testid="reviews-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Star className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Leave a review</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Tell us how your delivery went. Reviews are published once approved.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6" data-testid="review-form">
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your rating</span>
          <div className="mt-1">
            <StarRatingInput value={rating} onChange={setRating} />
          </div>
        </div>

        {delivered.length > 0 && (
          <div>
            <label htmlFor="review-package" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Which delivery is this about? <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <select id="review-package" className={inputClass} value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">General experience</option>
              {delivered.map((pkg) => (
                <option key={pkg.id} value={String(pkg.id)} disabled={reviewedPackageIds.has(pkg.id as number)}>
                  #{pkg.trackingNo}{pkg.description ? ` — ${pkg.description}` : ''}{reviewedPackageIds.has(pkg.id as number) ? ' (reviewed)' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="review-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Title <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input id="review-title" className={inputClass} value={title} maxLength={TITLE_LIMIT} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Fast and friendly" />
        </div>

        <div>
          <label htmlFor="review-body" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your review</label>
          <textarea id="review-body" rows={4} className={inputClass} value={body} maxLength={BODY_LIMIT} onChange={(e) => setBody(e.target.value)} placeholder="What went well? What could we do better?" />
          <p className="mt-1 text-right text-xs text-gray-400">{body.length}/{BODY_LIMIT}</p>
        </div>

        {formError && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{formError}</p>}

        <Button type="submit" variant="primary" fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
          Submit review
        </Button>
      </form>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your reviews</h2>
        {isLoading ? (
          <p className="mt-2 text-sm text-gray-500" data-testid="reviews-loading">Loading…</p>
        ) : reviews.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">You have not left a review yet.</p>
        ) : (
          <ul className="mt-3 space-y-3" data-testid="my-reviews">
            {reviews.map((review) => (
              <li key={review.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-0.5" role="img" aria-label={`${review.rating} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={16} className={n <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'} aria-hidden="true" />
                    ))}
                  </span>
                  <ReviewStatusBadge status={review.status} />
                </div>
                {review.title && <p className="mt-2 font-semibold text-gray-900 dark:text-white">{review.title}</p>}
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{review.body}</p>
                {review.package?.trackingNo && <p className="mt-2 text-xs text-gray-400">Package #{review.package.trackingNo}</p>}
                {review.status === 'rejected' && review.reviewNotes && (
                  <p className="mt-2 rounded bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300" data-testid="review-notes">
                    Note from {`the team`}: {review.reviewNotes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Reviews;
